import { BufferManagerWebGPU } from '../../BufferManagerWebGPU';
import { getMaterialTextureMask, MaterialUniformFlags } from '../MaterialUniforms';
import { CpuBuffer } from '../../CpuBuffer.js';
import { Megabytes } from '../../../globals.js';
import { MaterialChangeTracker } from '../../../scene/MaterialChangeTracker.js';

/** @import { Material } from '../../../../../thirdparty/three.js/three' */

const MaterialOffsets = {
  renderFlags: 0,
  diffuse: 1,
  specular: 2,
  shininess: 3,

  hatchParams: 4, // vec2f
  hatchTintIntensity: 6,
  heatmapSensorCountAndOffset: 7,

  SIZE_IN_FLOATS: 8
};

const Material2dOffsets = {
  selectionColor: 0, // vec4f
  viewportBounds: 4, // vec4f,

  hatchParams: 8, // vec2f,
  hatchTintIntensity: 10,
  renderFlags: 11,

  hasTexture: 12,
  hasLayers: 13,
  hasViewportClipping: 14,
  diffuse: 15,

  SIZE_IN_FLOATS: 16
};

// Manages a buffer with shared materials that are referenced by object uniforms.
// Currently, we share a single fixed-size buffer across all models and overlays.
//
// The buffer can be seen as a big array of MaterialUniform entries.
// An index into this array is called "material references". ObjectUniforms store a material reference per object.
export class MaterialUniformBuffer {

  /** @type {GPUBuffer|null} */
  #gpuBuffer = null;

  /** @type {GPUDevice} */
  #device = null;

  /** @type {BufferManagerWebGPU} */
  #bufferManager;

  // Small CPU-Side buffer to store properties for a single material.
  // Only used temporarily for uploading to the larger GPU buffer.
  #materialCpu = new CpuBuffer(Math.max(
    MaterialOffsets.SIZE_IN_FLOATS, Material2dOffsets.SIZE_IN_FLOATS));

  // Material event handlers to sync material updates with the GPU buffer.
  #boundMaterialUpdateCallback;
  #boundRemoveMaterialUpdateCallbacks;

  /** @type {Map<Material, BufferAlloc>} */
  #allocs = new Map();

  #changeTracker = new MaterialChangeTracker();

  #defaultSelectionColor = new THREE.Vector4(0, 0, 1, 1);

  constructor(device) {
    this.#device = device;

    const bufferDesc = {
      label: 'Material Uniform Buffer',
      size: Megabytes(32),
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    };

    this.#bufferManager = new BufferManagerWebGPU(device, bufferDesc, { destroyEmptyBuffers: false });

    // NOTE: We keep the implementation simple by pre-allocating a material uniform buffer that is large enough to
    // hold > 1 million materials (see size constant in bump allocator) and assuming that this will be sufficient.
    // The more granular (and scalable) approach would be to allocate more, but smaller buffers on demand.
    // But this would require multiple bind groups. We would need to add code to switch the bind group inside the
    // render loop, which would require changes in different places and affect the overall flow / execution order.
    // Furthermore, we would need to keep track of the actual underlying buffer per material.
    const memoryBlock = this.#bufferManager.allocate(0); // Allocate a zero-size material to create an empty buffer
    this.#gpuBuffer = memoryBlock.buffer;

    // bind material event handlers
    this.#boundMaterialUpdateCallback = this.#materialUpdateCallback.bind(this);
    this.#boundRemoveMaterialUpdateCallbacks = this.#removeMaterialUpdateCallbacks.bind(this);
  }

  dtor() {
    // All materials that have event listeners will have an allocation
    // tracked in #allocs, via updateMaterial().
    for (const [material, alloc] of this.#allocs) {
      this.#removeListeners(material);
    }
    this.#allocs.clear();
    this.#allocs = null;
    this.#bufferManager.dtor();
    this.#bufferManager = null;
    this.#gpuBuffer = null;

    this.#materialCpu.dtor();
    this.#materialCpu = null;
    this.#changeTracker = null;
    this.#boundMaterialUpdateCallback = null;
    this.#boundRemoveMaterialUpdateCallbacks = null;
    this.#defaultSelectionColor = null;
  }

  /**
   * @returns {number} byteSize on GPU. Note that we track actually used memory, not including extra memory due to paging.
   */
  byteSize() {
    return this.computeUsedBytes().mbBytes;
  }

  /**
   * @param {Material} material
   * @returns {BufferAlloc|undefined}
   */
  getAlloc(material) {
    return this.#allocs.get(material);
  }

  /**
   * Ensures that space for the material is reserved in the GPU buffer for materials.
   * @param {THREE.Material} material
   * @returns {number} offset (in bytes) where the material is stored in the material GPUBuffer.
   */
  #initMaterialBuffer(material) {
    // If material was initialized already, just return its offset.
    let alloc = this.getAlloc(material);
    if (alloc) {
      return alloc.offset;
    }

    // Allocate space for the material in the GPU buffer.
    const size = material.is2d ?
    4 * Material2dOffsets.SIZE_IN_FLOATS :
    4 * MaterialOffsets.SIZE_IN_FLOATS;
    // Align materials to their struct size so we can index into the array
    // correctly in the shader.
    const memoryBlock = this.#bufferManager.allocate(size, size);
    this.#allocs.set(material, memoryBlock);

    // Add a dispose handler to free the material's buffer when the material is disposed.
    const disposeHandler = () => {
      // If this MaterialUniformBuffer has been destroyed, the buffer
      // manager no longer exists, and the memory has already been freed.
      if (this.#bufferManager) {
        this.#bufferManager.free(memoryBlock);
        this.#allocs.delete(material);
      }
      material.removeEventListener('dispose', disposeHandler);
    };
    material.addEventListener('dispose', disposeHandler);

    // in bytes
    return memoryBlock.offset;
  }

  /**
   *  @param {number} writeOffset - Offset in bytes in the GPUBuffer where to write the material
   *  @param {number} size - Size in bytes to write
   */
  #uploadMaterialBuffer(writeOffset, size) {
    this.#device.queue.writeBuffer(
      this.#gpuBuffer,
      writeOffset,
      this.#materialCpu.getCpuBuffer().buffer,
      0,
      size
    );
  }

  // Makes sure that a buffer slot is reserved to store the properties of the given material in the GPUBuffer.
  // Returns the "materialRef", i.e. an index that is stored in the per-object uniforms to reference this material.
  acquireMaterial(material) {
    const byteOffset = this.#initMaterialBuffer(material);
    const size = 4 * (material.is2d ?
    Material2dOffsets.SIZE_IN_FLOATS :
    MaterialOffsets.SIZE_IN_FLOATS);
    return byteOffset / size;
  }

  // Compose material flags into int value for storage in the UniformBuffer
  #getMaterialFlags(material, materialTextureMask) {
    const isGhosted = material.opacity < 0 ? 1 : 0;
    const doNotCut = material.doNotCut ? 2 : 0;
    const alphaTest = material.alphaTest > 0 ? 4 : 0;
    const hatchPattern = material.hatchPattern ? 8 : 0;
    const useEnvMap = !!material.envMap ? 16 : 0;
    const includeLevelCutPlanes = material.includeLevelCutPlanes ? 32 : 0;

    const invertFlags =
    (material.map?.invert ? MaterialUniformFlags.INVERT_DIFFUSE : 0) | (
    material.alphaMap?.invert ? MaterialUniformFlags.INVERT_ALPHA : 0);

    return isGhosted | doNotCut | alphaTest | hatchPattern | useEnvMap | includeLevelCutPlanes | materialTextureMask << 16 | invertFlags;
  }

  /**
   * Copies properties of a given material into the corresponding range of the GPUBuffer.
   *  @param {THREE.Material} material
   *  @param {Number}         materialTextureMask - Bitmask indicating which texture types are used (see getMaterialTextureMask() in MaterialUniforms)
   */
  updateMaterial(material, materialTextureMask) {

    // Only update if something changed (uses _changeCount, no exclusive dirty-flag ownership needed)
    if (!this.#changeTracker.materialChanged(material)) {
      return;
    }

    // Custom pipeline materials use their own uniform handling via uniformGroup.
    // They don't use the batched MaterialUniformBuffer.
    // Note: In theory, we could check this at the beginning. However, the most frequent case is still to just use standard material.
    //       By checking later, we only need this check in the less frequent "expensive anyway" case where an update is actually happing.
    if (material.needsCustomPipeline) {
      return;
    }

    // Make sure that a write index is allocated in the material GPUBuffer.
    const offset = this.#initMaterialBuffer(material);

    // Pack flags and texture mask into integer 0
    const flags = this.#getMaterialFlags(material, materialTextureMask);
    this.#materialCpu.setInt(MaterialOffsets.renderFlags, flags);

    // Convert colors to integer RGBA format
    //TODO: gammaInput is hardcoded to true here, it's normally a property of the renderer
    //and we need it off when tone mapping is not used.
    material.__gpuDiffuse = colorToInt(material.color, Math.abs(material.opacity));
    material.__gpuSpecular = material.specular ? colorToInt(material.specular, material.reflectivity) : 0;

    this.#materialCpu.setInt(MaterialOffsets.diffuse, material.__gpuDiffuse);
    this.#materialCpu.setInt(MaterialOffsets.specular, material.__gpuSpecular);

    // Write shininess (float)
    this.#materialCpu.setFloat(MaterialOffsets.shininess, material.shininess);

    // Optional hatch pattern parameters for cap surfaces
    if (material.hatchPattern) {
      this.#materialCpu.setVector2(MaterialOffsets.hatchParams, material.hatchParams);
      this.#materialCpu.setFloat(MaterialOffsets.hatchTintIntensity, material.hatchTintIntensity);
    }

    // Tandem-specific heatmap uniforms
    this.#materialCpu.setInt(MaterialOffsets.heatmapSensorCountAndOffset,
    ((material.heatmapSensorOffset ?? 0) << 8) + (material.heatmapSensorCount ?? 0));

    this.#uploadMaterialBuffer(offset, 4 * MaterialOffsets.SIZE_IN_FLOATS);

    // Only needed for legacy reasons: uniformsNeedsUpdate must not stay true forever.
    //
    // TODO: If possible, we should remove direct access to this flag completely and consistently use the change tracker instead,
    //       to avoid trouble with "unclear who resets it" traps.
    material.uniformsNeedUpdate = false;
  }

  /** Used for 2D LineShader materials
   * TODO: Most of this function is duplicated code from updateMaterial() - so maybe we can make it one.
   * @param {THREE.Material} material
   */
  updateMaterial2D(material) {
    if (!this.#changeTracker.materialChanged(material)) {
      return;
    }

    this.#initMaterialBuffer(material);

    // MaterialMask is not used for 2D materials (always 0)
    const flags = this.#getMaterialFlags(material, 0);
    this.#materialCpu.setInt(Material2dOffsets.renderFlags, flags);
    const opacity2d = material.uniforms?.["opacity"]?.value ?? material.opacity;
    this.#materialCpu.setInt(Material2dOffsets.diffuse,
    colorToInt({ r: 0, g: 0, b: 0 }, opacity2d));

    if (material.hatchPattern) {
      this.#materialCpu.setVector2(Material2dOffsets.hatchParams, material.hatchParams);
      this.#materialCpu.setFloat(Material2dOffsets.hatchTintIntensity, material.hatchTintIntensity);
    }

    const hasTexture = material.uniforms["tRaster"]?.value ? 1 : 0;
    this.#materialCpu.setInt(Material2dOffsets.hasTexture, hasTexture);

    const selectionColor = material.uniforms.selectionColor?.value || this.#defaultSelectionColor;
    this.#materialCpu.setVector4(Material2dOffsets.selectionColor, selectionColor);

    const hasLayers = material.uniforms['tLayerMask']?.value ? 1 : 0;
    this.#materialCpu.setInt(Material2dOffsets.hasLayers, hasLayers);

    const hasViewportClipping = material.defines['VIEWPORT_CLIPPING'];
    this.#materialCpu.setInt(Material2dOffsets.hasViewportClipping, hasViewportClipping);
    const viewportBounds = material.uniforms['viewportBounds']?.value;
    if (hasViewportClipping && viewportBounds) {
      this.#materialCpu.setVector4(Material2dOffsets.viewportBounds, viewportBounds);
    }

    const alloc = this.getAlloc(material);
    this.#uploadMaterialBuffer(alloc.offset, 4 * Material2dOffsets.SIZE_IN_FLOATS);

    // Legacy flag: see comment in updateMaterial().
    material.uniformsNeedUpdate = false;
  }

  /**
   * The MaterialUniformBuffer stores a GPU-Side array of materials.
   * The returned materialRef is an index into that array. It is used in the per-object uniforms to reference the given material.
   * @param {THREE.Material} material
   * @returns {Number} materialRef - Index into the MaterialUniformBuffer
   */
  getMaterialRef(material) {
    const alloc = this.getAlloc(material);
    const size = 4 * (material.is2d ?
    Material2dOffsets.SIZE_IN_FLOATS :
    MaterialOffsets.SIZE_IN_FLOATS);
    return alloc.offset / size;
  }

  /** @returns {GPUBuffer} */
  getGPUBuffer() {
    return this.#gpuBuffer;
  }

  /**
   * Initializes a material update hook on the material, to update material uniforms whenever the material changes.
   * The function will also upload the material uniforms when it encounters a material for the first time.
   * If the update hook has already been configured, this function is a no-op.
   *
   * Note: Use this only for materials that are referenced in the main scene, e.g. in RenderBatches.
   * Three.js scenes (scene, sceneAfter, overlays, 2d scenes, ...) have to update materials in the render loop via
   * setOneMaterialData(2D).
   * @param {THREE.Material} material The material that should be watched for updates.
   * @param {Number} materialTextureMask The material's texture mask, as returned by initMaterialBindings.
   */
  initMaterialUpdateHook(material, materialTextureMask) {
    // Custom pipeline materials manage their own uniforms via uniformGroup,
    // so skip event registration and batched uniform handling entirely.
    if (material.needsCustomPipeline) {
      return;
    }

    if (!material.hasEventListener('update', this.#boundMaterialUpdateCallback)) {
      material.addEventListener('update', this.#boundMaterialUpdateCallback);
      material.addEventListener('dispose', this.#boundRemoveMaterialUpdateCallbacks);

      // Ensure initial upload (materialChanged returns true on first encounter).
      this.updateMaterial(material, materialTextureMask);
    }
  }

  /**
   * A callback that is invoked as a material's update handler.
   * @param {*} event The update event. The material can be accessed via event.target.
   */
  #materialUpdateCallback(event) {

    // TODO: In case needsUpdate and uniformsNeedUpdate are both set, the callback would fire twice
    //       and would also upload twice, because it is synchronously triggered.
    //       It's currently not causing a problem, but isn't ideal either.
    const material = event.target;
    const materialTextureMask = getMaterialTextureMask(material);
    this.updateMaterial(material, materialTextureMask);
  }

  /**
   * A callback that is invoked when a material is disposed.
   * @param {*} event
   */
  #removeMaterialUpdateCallbacks(event) {
    const material = event.target;
    this.#removeListeners(material);
  }

  /**
   * @param {Material} material
   */
  #removeListeners(material) {
    material.removeEventListener('update', this.#boundMaterialUpdateCallback);
    material.removeEventListener('dispose', this.#boundRemoveMaterialUpdateCallbacks);
  }
};

/** Convert color object into Uint32 rgba color
 * @param {THREE.Color} color   - r,g,b in [0,1]
 * @param {number}      opacity - opacity in [0,1]
 * @returns {number} uint32 color (format 0xAABBGGRR)
 */
export function colorToInt(color, opacity) {
  let r = color.r;
  let g = color.g;
  let b = color.b;

  // gamma is applied by the shader, so we don't need it here.

  return r * 255 | g * 255 << 8 | b * 255 << 16 | opacity * 255 << 24;
}